// Phase C10 — full in-flight ticket state for the New Ticket page.
//
// What this context owns:
//   • The server's TicketDetailDto (the canonical persisted state).
//   • Autosave bookkeeping (isSaving / saveError / lastSavedAt) and the
//     debounced PATCH that backs the left-rail / centre-pane edits.
//   • The SSE weight stream feed (liveWeightKg, liveWeightStable) so the
//     centre pane can render a live readout and the right rail can react
//     to stability changes without prop-drilling.
//   • Per-line resolved-pricing snapshots from POST /wb/pricing/resolve
//     (server is canonical pricer — never compute on the client).
//
// Architectural notes pinned by WEIGHBRIDGE_SERVER_CONTRACT.md §C10:
//   • POST /wb/tickets REQUIRES customer_enc_id. The Start modal collects
//     direction/mode/yard/weighbridge locally and stages them on
//     `pendingCreateInput`. The actual POST fires from
//     createTicket(input) — typically when the operator selects a
//     customer in the left rail and that completes the required-field
//     set.
//   • PATCH is "full replacement" for `lines` — autosave must always
//     send the full lines array when any line has changed.
//   • Barrier actions returned by complete are NEVER dispatched via HTTP
//     here; the caller logs them and shows a toast (the agent WebSocket
//     protocol is out of scope for C10).

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { extractErrorMessage } from '../../../utils/errorMessage';
import type {
  PaymentMethod,
  TicketDetailDto,
  TicketDirection,
  TicketLineDto,
  TicketLineMode,
  UnitPriceBasis,
  WeighingMode,
  WeightMethod,
  WeightType,
} from '../hooks/useWbTicket';

// -- Input / response DTOs (mirror server contract §C10) -------------------

/**
 * Body for POST /wb/tickets. `customer_enc_id` is required server-side, so
 * the caller (Start modal + customer pick in left rail) must have a
 * customer pinned before invoking createTicket().
 */
export interface CreateTicketInput {
  direction: TicketDirection;
  weighing_mode: WeighingMode;
  yard_enc_id: string;
  weighbridge_enc_id?: string;
  customer_enc_id: string;
  haulier_enc_id?: string;
  vehicle_enc_id?: string;
  driver_enc_id?: string;
  notes?: string;
  internal_notes?: string;
  lines?: ReadonlyArray<TicketLineInput>;
}

/**
 * Per-line input shape for create / update. Mirrors the server's
 * `TicketLineInput` (see WEIGHBRIDGE_SERVER_CONTRACT.md §C10). When sent
 * via PATCH the server treats the `lines` array as a full replacement —
 * never send a partial slice.
 */
export interface TicketLineInput {
  /**
   * Stable line identity. Persisted server-side (wb_ticket_lines.uid) so that
   * re-opening a ticket restores the correct active-line selection. Lines
   * created before migration 185 return null and fall back to `srv-${id}`.
   */
  uid?: string;
  product_enc_id?: string;
  mode: TicketLineMode;
  quantity: number;
  /** Per-line weighing (weight-mode lines). Server derives net + quantity. */
  gross_weight_kg?: number | null;
  tare_weight_kg?: number | null;
  net_weight_kg?: number | null;
  unit_price: number;
  unit_price_basis?: UnitPriceBasis;
  price_basis_ref_id?: string;
  override_reason?: string;
  ewc_code?: string;
  is_custom?: boolean;
  custom_description?: string;
}

/**
 * Body for PATCH /wb/tickets/:enc_id. All fields optional; the server
 * requires at least one. Nullable variants set to null clear the field.
 * direction/weighing_mode are immutable post-create — deliberately
 * omitted here.
 */
export interface UpdateTicketInput {
  yard_enc_id?: string;
  weighbridge_enc_id?: string | null;
  customer_enc_id?: string;
  haulier_enc_id?: string | null;
  vehicle_enc_id?: string | null;
  driver_enc_id?: string | null;
  notes?: string | null;
  internal_notes?: string | null;
  gross_weight_kg?: number | null;
  tare_weight_kg?: number | null;
  /** Explicit operator transition: draft → open ("Save & Finish"/"Mark as Open"). */
  status?: 'open';
  direction?: TicketDirection;
  lines?: ReadonlyArray<TicketLineInput>;
}

/**
 * Body for POST /wb/tickets/:enc_id/weight-events. The server discriminates
 * gross vs tare via `weight_type`; method follows the wire enum
 * (hardware/manual/stored).
 */
export interface CreateWeightEventInput {
  weight_type: WeightType;
  weight_kg: number;
  /** Target a specific weight-mode line's gross/tare cell (per-line model). */
  ticket_line_id?: string;
  weighbridge_enc_id?: string;
  method: WeightMethod;
  raw_indicator_data?: string;
  is_stable?: boolean;
  attempt_no?: number;
  /** Minted by captureWeight() itself — callers never set this. */
  idempotency_key?: string;
}

/** Body for POST /wb/tickets/:enc_id/complete. */
export interface CompleteTicketInput {
  payment_method: PaymentMethod;
  paid_amount: number;
  notes?: string;
}

/**
 * A barrier action queued by ticket-complete. The server returns the list
 * for the local on-prem agent to act on — the client never dispatches
 * these over HTTP. C10 logs them to the console and surfaces a toast.
 */
export interface BarrierActionDto {
  barrier_enc_id: string;
  target_state: string;
  hold_ms: number;
}

export interface CompleteTicketResponse {
  ticket: TicketDetailDto;
  barrier_actions: BarrierActionDto[];
  credit_limit_warning: boolean;
}

/** Body for POST /wb/pricing/resolve. Server is the canonical pricer. */
export interface PriceResolveInput {
  customer_enc_id: string;
  product_enc_id: string;
  quantity?: number;
  manual_override_price?: number;
  manual_override_reason?: string;
}

/**
 * Weight snapshot of the line currently selected for weight capture. The
 * line editor (WbTicketCentrePaneLines) owns the editable line objects, so
 * it publishes the active line's weights here for the sibling weight pane
 * (WbTicketCentrePaneWeight) to render. Null when no line is selected.
 */
export interface ActiveLineWeights {
  gross_weight_kg: number | null;
  tare_weight_kg: number | null;
  net_weight_kg: number | null;
}

/** Response for POST /wb/pricing/resolve. */
export interface PriceResolutionResponse {
  unit_price_ex_vat: number;
  vat_rate_id: string | null;
  vat_rate_snapshot_pct: number | null;
  vat_amount: number;
  unit_price_inc_vat: number;
  basis: 'manual_override' | 'contract' | 'price_band' | 'general_public';
  basis_ref_id: string | null;
  basis_label: string;
  formula_used: string | null;
  subtotal_ex_vat: number | null;
  subtotal_inc_vat: number | null;
}

// -- Internal state ---------------------------------------------------------

interface TicketResponse {
  ticket: TicketDetailDto;
}

/**
 * Local-only staging of the Start-modal selections before the ticket has
 * been POSTed (a ticket can't exist server-side until a customer is
 * picked; see §C10 - customer_enc_id is required).
 *
 * Kept in context so the left rail can read these to know which
 * direction/mode the operator picked, and the right rail can decide
 * whether the "Complete ticket" button should be enabled (it never is
 * before the POST lands — there's no ticket yet to complete).
 */
export interface PendingTicketShell {
  direction: TicketDirection;
  weighing_mode: WeighingMode;
  yard_enc_id: string;
  weighbridge_enc_id?: string;
}

interface WbTicketContextValue {
  // -- Persisted server state ---------------------------------------------
  ticket: TicketDetailDto | null;

  // -- Staged Start-modal selections (only set before POST) ---------------
  pendingShell: PendingTicketShell | null;
  setPendingShell: (shell: PendingTicketShell | null) => void;

  // -- Save bookkeeping (manual-save model) -------------------------------
  isSaving: boolean;
  saveError: string | null;
  lastSavedAt: Date | null;
  /** True when there are buffered edits not yet persisted via saveTicket. */
  isDirty: boolean;

  // -- Resolved pricing (keyed by stable line key, not array index) -------
  resolvedPrices: Readonly<Record<string, PriceResolutionResponse>>;
  resolvingPriceForLine: ReadonlySet<string>;
  /**
   * Drop a stale resolved-price entry when its line is removed, so the map
   * doesn't accumulate keys for lines that no longer exist.
   */
  clearResolvedPrice: (lineKey: string) => void;

  // -- Active-line selection (per-line weight capture) --------------------
  /** Editor uid of the line currently selected for weight capture (or null). */
  activeLineUid: string | null;
  /** Select / deselect the active line — null clears the selection. */
  setActiveLineUid: (uid: string | null) => void;
  /**
   * Weight snapshot of the active line, published by the line editor so the
   * sibling weight pane can show the selected line's gross/tare/net. Null
   * when no line is selected.
   */
  activeLineWeights: ActiveLineWeights | null;
  setActiveLineWeights: (weights: ActiveLineWeights | null) => void;
  /**
   * Write a live weight into the currently-active line's gross/tare cell and
   * (after a tare) auto-advance the selection to the next un-weighed line.
   * The line editor owns the local editor-line state, so it registers the
   * actual implementation via registerActiveLineCapture; this is the sibling
   * weight pane's entry point. No-op when nothing has been registered.
   */
  captureActiveLine: (type: 'gross' | 'tare', weightKg: number) => void;
  /** Editor hook: register (or clear, with null) the active-line capture impl. */
  registerActiveLineCapture: (
    fn: ((type: 'gross' | 'tare', weightKg: number) => void) | null,
  ) => void;
  /**
   * True when the line editor currently holds at least one weight-mode line
   * (including freshly-added blank ones the autosave PATCH hasn't sent yet).
   * The weight pane uses this to decide between the always-visible per-line
   * capture card and the legacy ticket-level capture widget. Published by the
   * editor (it owns the local lines); blank weight lines never reach
   * `ticket.lines`, so this can't be derived from the persisted DTO.
   */
  hasWeightLines: boolean;
  /** Editor hook: publish whether any weight-mode line is present. */
  setHasWeightLines: (present: boolean) => void;

  /** True when the current tare_weight_kg was loaded from the selected vehicle's stored tare. Cleared on manual tare edit or vehicle deselect. */
  tareAutoFilled: boolean;
  setTareAutoFilled: (value: boolean) => void;

  // -- Actions ------------------------------------------------------------
  /** Hydrate context from an existing draft (e.g. `?from=enc_id` flow). */
  hydrateFromTicket: (ticket: TicketDetailDto) => void;
  /** POST /wb/tickets. Caller must have direction/mode/yard/customer. */
  createTicket: (input: CreateTicketInput) => Promise<TicketDetailDto>;
  /** Patch state optimistically + buffer the change for the next save. */
  patchTicket: (input: UpdateTicketInput) => void;
  /** Flush the buffered patch now (explicit Save button). Resolves true on
   * success / nothing-to-save, false when the save failed. */
  saveTicket: () => Promise<boolean>;
  /** POST /wb/tickets/:enc_id/weight-events (gross/tare/verify). */
  captureWeight: (input: CreateWeightEventInput) => Promise<TicketDetailDto>;
  /** True while a captureWeight() POST is outstanding — capture triggers
   *  (buttons) should disable themselves until this clears. */
  isCapturingWeight: boolean;
  /** POST /wb/tickets/:enc_id/complete. Returns barrier_actions + warning. */
  completeTicket: (
    input: CompleteTicketInput,
  ) => Promise<CompleteTicketResponse>;
  /** Debounced POST /wb/pricing/resolve for one line (keyed by stable key). */
  resolveLinePrice: (lineKey: string, input: PriceResolveInput) => void;
  /** Reset every field — called on page unmount / new ticket. */
  resetTicket: () => void;
}

const WbTicketContext = createContext<WbTicketContextValue | null>(null);

// -- Provider ---------------------------------------------------------------

export function WbTicketProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  // -- Persisted server state -------------------------------------------
  const [ticket, setTicket] = useState<TicketDetailDto | null>(null);
  const [pendingShell, setPendingShellState] =
    useState<PendingTicketShell | null>(null);

  // -- Save bookkeeping (manual-save model) -----------------------------
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [isDirty, setIsDirty] = useState(false);

  // -- Resolved pricing (keyed by stable line key) ----------------------
  const [resolvedPrices, setResolvedPrices] = useState<
    Record<string, PriceResolutionResponse>
  >({});
  const [resolvingPriceForLine, setResolvingPriceForLine] = useState<
    Set<string>
  >(() => new Set());

  // -- Active-line selection (per-line weight capture) ------------------
  const [activeLineUid, setActiveLineUidState] = useState<string | null>(null);
  const [activeLineWeights, setActiveLineWeightsState] =
    useState<ActiveLineWeights | null>(null);

  // -- Refs used by debounced operations --------------------------------
  const ticketRef = useRef<TicketDetailDto | null>(null);
  ticketRef.current = ticket;

  // Pending PATCH payload merged across multiple patchTicket() calls
  // within one debounce window — keeps the wire shape compact.
  const pendingPatchRef = useRef<UpdateTicketInput>({});
  const patchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Per-line debounce timers + latest payloads for price resolve (keyed by
  // stable line key, not array index).
  const priceTimersRef = useRef<
    Map<string, ReturnType<typeof setTimeout>>
  >(new Map());
  const priceInputsRef = useRef<Map<string, PriceResolveInput>>(new Map());

  // Monotonic mutation sequence. Every server-mutating call takes the next
  // number when ISSUED; a response only applies via setTicket when its
  // sequence is >= the latest one already applied — so an out-of-order or
  // stale PATCH/capture response can never clobber newer state.
  const mutationSeqRef = useRef(0);
  const appliedSeqRef = useRef(0);

  // Weight-capture double-submit guard: a second capture trigger (e.g. an
  // operator double-click) while one POST is still outstanding must not fire
  // another — it has to wait for the first to settle (2xx or error).
  const captureInFlightRef = useRef(false);
  const [isCapturingWeight, setIsCapturingWeight] = useState(false);

  const setPendingShell = useCallback((shell: PendingTicketShell | null) => {
    setPendingShellState(shell);
  }, []);

  const setActiveLineUid = useCallback((uid: string | null) => {
    setActiveLineUidState(uid);
  }, []);

  // Imperative bridge: the line editor registers a handler here so the sibling
  // weight pane can write a captured weight into the active line (and trigger
  // auto-advance) WITHOUT lifting the editor's local line state into context —
  // that decoupling is load-bearing (it's what stops a server echo clobbering
  // the operator's in-flight typing).
  const activeLineCaptureRef = useRef<
    ((type: 'gross' | 'tare', weightKg: number) => void) | null
  >(null);
  const registerActiveLineCapture = useCallback(
    (fn: ((type: 'gross' | 'tare', weightKg: number) => void) | null) => {
      activeLineCaptureRef.current = fn;
    },
    [],
  );
  const captureActiveLine = useCallback(
    (type: 'gross' | 'tare', weightKg: number) => {
      activeLineCaptureRef.current?.(type, weightKg);
    },
    [],
  );

  const [hasWeightLines, setHasWeightLinesState] = useState(false);
  const setHasWeightLines = useCallback((present: boolean) => {
    setHasWeightLinesState((prev) => (prev === present ? prev : present));
  }, []);

  const [tareAutoFilled, setTareAutoFilledState] = useState(false);
  const setTareAutoFilled = useCallback((value: boolean) => {
    setTareAutoFilledState(value);
  }, []);

  // Publish the active line's weights. Guarded so an unchanged snapshot
  // doesn't churn context (the editor re-publishes on every line edit).
  const setActiveLineWeights = useCallback(
    (weights: ActiveLineWeights | null) => {
      setActiveLineWeightsState((prev) => {
        if (prev === weights) return prev;
        if (prev === null || weights === null) return weights;
        if (
          prev.gross_weight_kg === weights.gross_weight_kg &&
          prev.tare_weight_kg === weights.tare_weight_kg &&
          prev.net_weight_kg === weights.net_weight_kg
        ) {
          return prev;
        }
        return weights;
      });
    },
    [],
  );

  const clearResolvedPrice = useCallback((lineKey: string) => {
    setResolvedPrices((prev) => {
      if (!(lineKey in prev)) return prev;
      const next = { ...prev };
      delete next[lineKey];
      return next;
    });
  }, []);

  // Apply a server ticket only if this response isn't older than the most
  // recently applied mutation. Returns whether it was applied.
  const applyTicketIfFresh = useCallback(
    (seq: number, next: TicketDetailDto): boolean => {
      if (seq < appliedSeqRef.current) return false;
      appliedSeqRef.current = seq;
      setTicket((prev) => {
        // Carry the client-only line `uid`s from the optimistic state onto the
        // server echo (which strips them — the wire schema is `.strict()`). The
        // right-rail pricing summary keys resolvedPrices by uid, so without this
        // a Save/capture echo would orphan every resolved price and the totals
        // would collapse to the unpriced draft `line_total` (0). Lines are sent
        // and returned in order, so a positional carry is exact.
        const withUids = carryLineUids(prev, next);
        // Freeze the ticket enc_id to the FIRST one we applied (from
        // create/hydrate). The server re-encrypts the numeric id on every
        // response (non-deterministic ciphertext), so taking the echo's enc_id
        // would make the ticket's identity appear to change on every PATCH.
        // That re-fires every enc_id-keyed guard — the page's `?from=`
        // hydration effect and the centre-pane line resync — which then wipe
        // the operator's just-picked customer / product. The original enc_id
        // still decrypts to the same numeric id, so it stays valid for every
        // subsequent API call (PATCH/capture/complete/delete).
        const stableEncId = prev?.enc_id ?? withUids.enc_id;
        const frozen =
          withUids.enc_id === stableEncId
            ? withUids
            : { ...withUids, enc_id: stableEncId };
        // Re-apply any STILL-BUFFERED local edits on top of the echo. The echo
        // confirms server state as of the patch that was FLUSHED (flushPatch
        // clears the buffer before awaiting), so anything left in the buffer is
        // an edit the operator made AFTER that patch was issued — e.g. picking
        // a customer/product while a prior autosave PATCH is in flight. The seq
        // guard only blocks out-of-order echoes; it can't protect an optimistic
        // change that never bumped the sequence. Without this re-apply, that
        // in-flight echo reverts the just-picked value (the round-4 regression).
        const pending = pendingPatchRef.current;
        return Object.keys(pending).length === 0
          ? frozen
          : mergeTicketWithPatch(frozen, pending);
      });
      return true;
    },
    [],
  );

  // -- hydrateFromTicket (used by ?from=enc_id flow) --------------------
  const hydrateFromTicket = useCallback((next: TicketDetailDto) => {
    setTicket(next);
    setPendingShellState(null);
    setSaveError(null);
    setLastSavedAt(null);
    setTareAutoFilledState(
      next.tare_method === 'stored' && next.tare_weight_kg !== null,
    );
  }, []);

  // -- flushPatch (drains the debounced autosave) -----------------------
  //
  // Issues a single PATCH against the ticket as it exists right now. If no
  // ticket exists yet (Start modal still open, or the create POST is still
  // in flight) we DON'T discard the pending payload — it stays buffered so
  // createTicket can drain it once the ticket lands. That prevents a fast
  // edit made during the create round-trip from being silently lost.
  // (Defined before createTicket so the latter can drain via this fn.)
  const flushPatch = useCallback(async (): Promise<boolean> => {
    const t = ticketRef.current;
    if (t === null) return false; // keep pendingPatchRef buffered for the drain
    const payload = pendingPatchRef.current;
    pendingPatchRef.current = {};
    if (Object.keys(payload).length === 0) return true; // nothing to save
    const seq = (mutationSeqRef.current += 1);
    setIsSaving(true);
    setSaveError(null);
    try {
      const res = await apiClient.patch<TicketResponse, UpdateTicketInput>(
        endpoints.wb.ticket(t.enc_id),
        payload,
      );
      // Drop the response if a newer mutation has already been applied —
      // an out-of-order PATCH must never clobber fresher state.
      if (applyTicketIfFresh(seq, res.data.ticket)) {
        setLastSavedAt(new Date());
      }
      // Only mark clean if no new edits were queued during the round-trip.
      if (Object.keys(pendingPatchRef.current).length === 0) setIsDirty(false);
      return true;
    } catch (err) {
      setSaveError(extractErrorMessage(err) ?? 'Save failed.');
      // Re-buffer the failed payload so a manual retry doesn't lose edits
      // (any edits made during the in-flight save win on conflict).
      pendingPatchRef.current = mergePatchPayloads(
        payload,
        pendingPatchRef.current,
      );
      setIsDirty(true);
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [applyTicketIfFresh]);

  // -- createTicket -----------------------------------------------------
  const createTicket = useCallback(
    async (input: CreateTicketInput): Promise<TicketDetailDto> => {
      const seq = (mutationSeqRef.current += 1);
      setIsSaving(true);
      setSaveError(null);
      try {
        const res = await apiClient.post<TicketResponse, CreateTicketInput>(
          endpoints.wb.tickets,
          input,
        );
        const next = res.data.ticket;
        applyTicketIfFresh(seq, next);
        // Make the new ticket visible to flushPatch synchronously — the
        // setTicket above + the ref refresh on the next render haven't run
        // yet, so set the ref directly so a queued pre-create patch drains
        // against the real ticket rather than the null it was buffered under.
        ticketRef.current = next;
        setPendingShellState(null);
        setLastSavedAt(new Date());
        // Drain any patch buffered while the ticket didn't exist yet.
        if (Object.keys(pendingPatchRef.current).length > 0) {
          await flushPatch();
        }
        return next;
      } catch (err) {
        setSaveError(extractErrorMessage(err) ?? 'Failed to create ticket.');
        throw err;
      } finally {
        setIsSaving(false);
      }
    },
    [applyTicketIfFresh, flushPatch],
  );

  const patchTicket = useCallback(
    (input: UpdateTicketInput) => {
      // Optimistic merge into local ticket state so the UI reflects the change
      // immediately.
      setTicket((prev) =>
        prev === null ? prev : mergeTicketWithPatch(prev, input),
      );
      // Buffer the change and mark the ticket dirty.
      pendingPatchRef.current = mergePatchPayloads(
        pendingPatchRef.current,
        input,
      );
      setIsDirty(true);
      // Debounced autosave: flush 1500ms after the last edit so changes
      // persist without an explicit click. The Save button stays as an
      // immediate "flush now" (saveTicket cancels this timer first), and
      // isDirty still drives the unsaved-changes affordance in between.
      if (patchTimerRef.current !== null) clearTimeout(patchTimerRef.current);
      patchTimerRef.current = setTimeout(() => {
        patchTimerRef.current = null;
        void flushPatch();
      }, 1500);
    },
    [flushPatch],
  );

  const saveTicket = useCallback(async (): Promise<boolean> => {
    // Flush now — cancel any pending debounced autosave so we don't double-fire.
    if (patchTimerRef.current !== null) {
      clearTimeout(patchTimerRef.current);
      patchTimerRef.current = null;
    }
    return flushPatch();
  }, [flushPatch]);

  // -- captureWeight ---------------------------------------------------
  const captureWeight = useCallback(
    async (input: CreateWeightEventInput): Promise<TicketDetailDto> => {
      const t = ticketRef.current;
      if (t === null) {
        throw new Error('Cannot capture a weight: no ticket exists yet.');
      }
      if (captureInFlightRef.current) {
        throw new Error('A weight capture is already in progress.');
      }
      // Minted at the MOMENT the operator triggers this capture — stable for
      // the life of this request (including any offline/queued retry a
      // caller layers on top), but a NEW trigger always gets a NEW nonce.
      const idempotencyKey = crypto.randomUUID();
      captureInFlightRef.current = true;
      setIsCapturingWeight(true);
      try {
        const seq = (mutationSeqRef.current += 1);
        const res = await apiClient.post<TicketResponse, CreateWeightEventInput>(
          endpoints.wb.ticketWeightEvents(t.enc_id),
          { ...input, idempotency_key: idempotencyKey },
        );
        const next = res.data.ticket;
        // Only commit if no newer mutation landed first; still return the
        // server ticket to the caller for error/toast handling regardless.
        applyTicketIfFresh(seq, next);
        return next;
      } finally {
        captureInFlightRef.current = false;
        setIsCapturingWeight(false);
      }
    },
    [applyTicketIfFresh],
  );

  // -- completeTicket --------------------------------------------------
  const completeTicket = useCallback(
    async (input: CompleteTicketInput): Promise<CompleteTicketResponse> => {
      const t = ticketRef.current;
      if (t === null) {
        throw new Error('Cannot complete a ticket: no ticket exists yet.');
      }
      // Flush any pending autosave first so the server completes against
      // the latest party/notes/lines state. We deliberately swallow the
      // autosave's bookkeeping side-effects — complete() owns the
      // user-visible status from here on.
      if (patchTimerRef.current !== null) {
        clearTimeout(patchTimerRef.current);
        patchTimerRef.current = null;
      }
      await flushPatch();
      const seq = (mutationSeqRef.current += 1);
      const res = await apiClient.post<
        CompleteTicketResponse,
        CompleteTicketInput
      >(endpoints.wb.ticketComplete(t.enc_id), input);
      applyTicketIfFresh(seq, res.data.ticket);
      return res.data;
    },
    [flushPatch, applyTicketIfFresh],
  );

  // -- resolveLinePrice (debounced per line) ---------------------------
  const resolveLinePrice = useCallback(
    (lineKey: string, input: PriceResolveInput) => {
      priceInputsRef.current.set(lineKey, input);
      const existing = priceTimersRef.current.get(lineKey);
      if (existing !== undefined) clearTimeout(existing);
      // 400 ms debounce — matches the contract doc's 300-500ms guidance.
      const timer = setTimeout(async () => {
        priceTimersRef.current.delete(lineKey);
        const payload = priceInputsRef.current.get(lineKey);
        if (payload === undefined) return;
        setResolvingPriceForLine((prev) => {
          const next = new Set(prev);
          next.add(lineKey);
          return next;
        });
        try {
          const res = await apiClient.post<
            PriceResolutionResponse,
            PriceResolveInput
          >(endpoints.wb.pricingResolve, payload);
          setResolvedPrices((prev) => ({ ...prev, [lineKey]: res.data }));
        } catch {
          // Surface failures via the spinner clearing — the centre pane
          // can choose to fall back to manual entry if resolve never
          // completes. We don't toast here because the line-level UI
          // already shows a "—" / retry affordance.
        } finally {
          setResolvingPriceForLine((prev) => {
            const next = new Set(prev);
            next.delete(lineKey);
            return next;
          });
        }
      }, 400);
      priceTimersRef.current.set(lineKey, timer);
    },
    [],
  );

  // -- resetTicket -----------------------------------------------------
  const resetTicket = useCallback(() => {
    if (patchTimerRef.current !== null) {
      clearTimeout(patchTimerRef.current);
      patchTimerRef.current = null;
    }
    for (const timer of priceTimersRef.current.values()) clearTimeout(timer);
    priceTimersRef.current.clear();
    priceInputsRef.current.clear();
    pendingPatchRef.current = {};
    mutationSeqRef.current = 0;
    appliedSeqRef.current = 0;
    setTicket(null);
    setPendingShellState(null);
    setIsSaving(false);
    setSaveError(null);
    setLastSavedAt(null);
    setIsDirty(false);
    setResolvedPrices({});
    setResolvingPriceForLine(new Set());
    setActiveLineUidState(null);
    setActiveLineWeightsState(null);
    setHasWeightLinesState(false);
    setTareAutoFilledState(false);
  }, []);

  // Tear down any pending timers when the provider unmounts.
  useEffect(() => {
    return () => {
      if (patchTimerRef.current !== null) clearTimeout(patchTimerRef.current);
      for (const timer of priceTimersRef.current.values()) clearTimeout(timer);
    };
  }, []);

  const value = useMemo<WbTicketContextValue>(
    () => ({
      ticket,
      pendingShell,
      setPendingShell,
      isSaving,
      saveError,
      lastSavedAt,
      isDirty,
      resolvedPrices,
      resolvingPriceForLine,
      clearResolvedPrice,
      activeLineUid,
      setActiveLineUid,
      activeLineWeights,
      setActiveLineWeights,
      captureActiveLine,
      registerActiveLineCapture,
      hasWeightLines,
      setHasWeightLines,
      tareAutoFilled,
      setTareAutoFilled,
      hydrateFromTicket,
      createTicket,
      patchTicket,
      saveTicket,
      captureWeight,
      isCapturingWeight,
      completeTicket,
      resolveLinePrice,
      resetTicket,
    }),
    [
      ticket,
      pendingShell,
      setPendingShell,
      isSaving,
      saveError,
      lastSavedAt,
      isDirty,
      resolvedPrices,
      resolvingPriceForLine,
      clearResolvedPrice,
      activeLineUid,
      setActiveLineUid,
      activeLineWeights,
      setActiveLineWeights,
      captureActiveLine,
      registerActiveLineCapture,
      hasWeightLines,
      setHasWeightLines,
      tareAutoFilled,
      setTareAutoFilled,
      hydrateFromTicket,
      createTicket,
      patchTicket,
      saveTicket,
      captureWeight,
      isCapturingWeight,
      completeTicket,
      resolveLinePrice,
      resetTicket,
    ],
  );

  return (
    <WbTicketContext.Provider value={value}>
      {children}
    </WbTicketContext.Provider>
  );
}

export function useWbTicket(): WbTicketContextValue {
  const value = useContext(WbTicketContext);
  if (value === null) {
    throw new Error('useWbTicket must be used within a WbTicketProvider');
  }
  return value;
}

// -- Pure helpers (exported for unit testing) -------------------------------

/**
 * Carry the client-only line `uid`s from the previous (optimistic) ticket
 * onto a fresh server echo by position. The server strips `uid` (its line
 * schema is `.strict()`), but the right-rail pricing summary keys
 * resolvedPrices by uid — so a Save/capture echo must re-attach them or the
 * summary loses every resolved price. Lines round-trip in order (the server
 * assigns line_no 1..n in input order and returns them sorted), so position
 * `i` is a safe key. When the counts differ (a concurrent add/remove landed
 * between send and echo) we skip the carry rather than mis-attach.
 */
function carryLineUids(
  prev: TicketDetailDto | null,
  next: TicketDetailDto,
): TicketDetailDto {
  if (prev === null) return next;
  if (prev.lines.length !== next.lines.length) return next;
  let changed = false;
  const lines = next.lines.map((line, i) => {
    const uid = prev.lines[i]?.uid;
    if (uid === undefined || line.uid === uid) return line;
    changed = true;
    return { ...line, uid };
  });
  return changed ? { ...next, lines } : next;
}

/**
 * Merge a partial UpdateTicketInput into the optimistic view of the
 * ticket. Reference-type updates (haulier_enc_id=null) translate to the
 * matching nested object being cleared on the DTO. We don't try to
 * resolve enc_ids back to names — the next PATCH response replaces this
 * snapshot anyway.
 */
function mergeTicketWithPatch(
  ticket: TicketDetailDto,
  patch: UpdateTicketInput,
): TicketDetailDto {
  let next: TicketDetailDto = ticket;
  if (patch.notes !== undefined) {
    next = { ...next, notes: patch.notes };
  }
  if (patch.internal_notes !== undefined) {
    next = { ...next, internal_notes: patch.internal_notes };
  }
  if (patch.gross_weight_kg !== undefined) {
    next = { ...next, gross_weight_kg: patch.gross_weight_kg };
  }
  if (patch.tare_weight_kg !== undefined) {
    next = { ...next, tare_weight_kg: patch.tare_weight_kg };
  }

  // Lines: project the input array onto the optimistic ticket so consumers
  // that read `ticket.lines` (right-rail pricing summary) reflect the line
  // editor's current state immediately, not a full autosave cycle later.
  // We can't derive server-priced fields (vat/subtotal/line_total) or the
  // product name from an enc_id on the client, so those carry over from the
  // same-position prior line (or stay zero/null for brand-new lines) until
  // the PATCH response replaces this snapshot with the canonical pricing.
  if (patch.lines !== undefined) {
    const prevLines = next.lines;
    next = {
      ...next,
      lines: patch.lines.map((line, i) =>
        projectInputLineToDto(line, prevLines[i]),
      ),
    };
  }

  // Party / yard / weighbridge updates. For a non-null pick we reflect the
  // enc_id immediately (preserving the prior name/registration when the
  // enc_id is unchanged); a null clears the nested object. Either way the
  // next PATCH response re-hydrates the canonical nested objects + names.
  if (typeof patch.customer_enc_id === 'string') {
    next = {
      ...next,
      customer: {
        enc_id: patch.customer_enc_id,
        name:
          next.customer?.enc_id === patch.customer_enc_id
            ? next.customer.name
            : '',
      },
    };
  }
  if (patch.haulier_enc_id === null) {
    next = { ...next, haulier: null };
  } else if (typeof patch.haulier_enc_id === 'string') {
    next = {
      ...next,
      haulier: {
        enc_id: patch.haulier_enc_id,
        name:
          next.haulier?.enc_id === patch.haulier_enc_id
            ? next.haulier.name
            : '',
      },
    };
  }
  if (patch.vehicle_enc_id === null) {
    next = { ...next, vehicle: null };
  } else if (typeof patch.vehicle_enc_id === 'string') {
    next = {
      ...next,
      vehicle: {
        enc_id: patch.vehicle_enc_id,
        registration:
          next.vehicle?.enc_id === patch.vehicle_enc_id
            ? next.vehicle.registration
            : '',
      },
    };
  }
  if (patch.driver_enc_id === null) {
    next = { ...next, driver: null };
  } else if (typeof patch.driver_enc_id === 'string') {
    next = {
      ...next,
      driver: {
        enc_id: patch.driver_enc_id,
        name:
          next.driver?.enc_id === patch.driver_enc_id ? next.driver.name : '',
      },
    };
  }
  if (patch.weighbridge_enc_id === null) {
    next = { ...next, weighbridge: null };
  } else if (typeof patch.weighbridge_enc_id === 'string') {
    next = {
      ...next,
      weighbridge: {
        enc_id: patch.weighbridge_enc_id,
        name:
          next.weighbridge?.enc_id === patch.weighbridge_enc_id
            ? next.weighbridge.name
            : '',
      },
    };
  }
  return next;
}

/**
 * Best-effort projection of a TicketLineInput onto a TicketLineDto for the
 * optimistic ticket. Editable fields come from the input; server-derived
 * fields carry over from the same-position prior line when present.
 */
function projectInputLineToDto(
  input: TicketLineInput,
  prev: TicketLineDto | undefined,
): TicketLineDto {
  const sameProduct =
    prev?.product != null &&
    input.product_enc_id != null &&
    prev.product.enc_id === input.product_enc_id;
  return {
    // Carry the editor's stable line identity onto the optimistic DTO so the
    // right-rail pricing summary can key resolvedPrices by it. (Server echoes
    // have no uid — those fall back to the canonical line_total.)
    uid: input.uid,
    id: prev?.id ?? 0,
    line_enc_id: prev?.line_enc_id ?? '',
    line_no: prev?.line_no ?? 0,
    product: sameProduct ? prev!.product : null,
    product_name_snapshot:
      input.is_custom === true
        ? input.custom_description ?? null
        : sameProduct
          ? prev!.product_name_snapshot
          : null,
    product_code_snapshot: sameProduct ? prev!.product_code_snapshot : null,
    mode: input.mode,
    quantity: input.quantity,
    gross_weight_kg: input.gross_weight_kg ?? null,
    tare_weight_kg: input.tare_weight_kg ?? null,
    net_weight_kg: input.net_weight_kg ?? null,
    unit_price:
      input.unit_price_basis !== 'manual_override' &&
      input.unit_price === 0 &&
      (prev?.unit_price ?? 0) > 0
        ? prev!.unit_price
        : input.unit_price,
    unit_price_basis: input.unit_price_basis ?? prev?.unit_price_basis ?? null,
    price_basis_ref_id: prev?.price_basis_ref_id ?? null,
    override_reason: input.override_reason ?? null,
    ewc_code_snapshot: input.ewc_code ?? prev?.ewc_code_snapshot ?? null,
    vat_rate_id: prev?.vat_rate_id ?? null,
    vat_rate_snapshot_pct: prev?.vat_rate_snapshot_pct ?? null,
    vat_amount: prev?.vat_amount ?? 0,
    subtotal: prev?.subtotal ?? 0,
    line_total: prev?.line_total ?? 0,
    gross_image_url: prev?.gross_image_url ?? null,
    tare_image_url: prev?.tare_image_url ?? null,
    photo_image_url: prev?.photo_image_url ?? null,
  };
}

/**
 * Merge two partial UpdateTicketInput payloads, with `next` winning. This
 * is the in-flight buffer for the debounced PATCH; calling patchTicket
 * twice with overlapping fields should send the last-wins value.
 */
function mergePatchPayloads(
  prev: UpdateTicketInput,
  next: UpdateTicketInput,
): UpdateTicketInput {
  return { ...prev, ...next };
}
